import { GetString } from 'fluent-react';
import noop from 'lodash-es/noop';
import React, {
  RefObject, useState,
} from 'react';
import styled from 'styled-components';
import {
  Product,
  ProductClass,
  ProductLevel,
  ProductYear,
  TradeFlow,
  TreeMapInput,
  TreeMapType,
} from '../../graphQL/types/shared';
import { gql, useQuery } from '../../graphQL/useQuery';
import { SlideElementProps } from '../../presentationManager/PresentationManager';
import ErrorOverlay from '../../sharedComponents/GraphError';
import Loading from '../../sharedComponents/GraphLoading';
import Tooltips from '../../tree/hoverTooltips/TooltipsContainer';
import Chart from '../../tree/newChart/Chart';
import {
  NumCellsTier,
} from '../../tree/newChart/domIndependentDrawUtils';
import useLastSuccessfulFetch from '../../tree/newChart/useLastSuccesfulFetch';
import useDOMElemMonitoring from '../../tree/useDOMElemSizeMonitoringHook';
import {
  GraphAreaContainer,
} from '../Grid';
import { NewProduct } from '../newProducts';
import {
  TreeMapColorCriterion,
} from '../Utils';
import transform from './transform';

const NoNewProductContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  height: 100%;
`;

const NoNewProductSVG = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  height: 100%;

  svg {
    max-height: 100%;

    .cls-1 {
      fill: #5065a8;
    }

    .cls-2 {
      opacity: 0.08;
    }

    .cls-3 {
      fill: none;
      stroke: #fff;
      stroke-miterlimit: 10;
      stroke-width: 9px;
    }
  }
`;

const NoNewProductText = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TooltipContainer = styled(GraphAreaContainer)`
  pointer-events: none;
`;

const query = gql`
  query CountryProfilesTreeMap(
    $facet: TreeMapType!,
    $productClass: ProductClass!,
    $productLevel: ProductLevel!,
    $locationLevel: LocationLevel,
    $product: ID,
    $partner: ID,
    $location: ID!,
    $year: Int!,
  ) {
    treeMapAndTooltip: treeMap(
      facet: $facet,
      productClass: $productClass,
      year: $year,
      productLevel: $productLevel,
      locationLevel: $locationLevel,
      location: $location,
      product: $product,
      partner: $partner,
      mergePci: false,
    ) {
      ... on TreeMapProduct {
        product {
          code
          id
          shortName
          type: productType
          topLevelParent {
            id
          }
        }
        exportValue
        importValue
      }
    }

    allProductYear(year: $year, productClass: $productClass, productLevel: $productLevel) {
      product {
        id
      }
      pci
    }

    location: newProductsCountry(location: $location, year: $year) {
      newProducts {
        id
        topLevelParent {
          id
          shortName
          type: productType
        }
      }
    }
  }
`;

export interface FetchedDatum {
  exportValue: number;
  importValue: number;
  product: {
    id: Product['id'],
    shortName: Product['shortName'],
    topLevelParent: {
      id: Product['topLevelParent']['id'],
    }
    code: Product['code'],
    type: Product['productType'],
  };
}

export interface ProductYearDatum {
  product: {
    id: Product['id'],
  };
  pci: ProductYear['pci'];
}

export type Variables = TreeMapInput;

export interface SuccessResponse {
  treeMapAndTooltip: FetchedDatum[];
  allProductYear: ProductYearDatum[];
  location: null | {
    newProducts: NewProduct[];
  };
}
const filterNewProductsDataToTreeMap =
  (newProducts: NewProduct[], treeMapAndTooltip: FetchedDatum[]): FetchedDatum[] => {
  const newProductIdArray = newProducts.map(product => product.id);
  const newProductArray = treeMapAndTooltip.filter(curr => newProductIdArray.includes(curr.product.id));
  return newProductArray;
};

interface ExtraInputs {
  countryId: string;
  colorCriterion: TreeMapColorCriterion;
  tradeFlow: TradeFlow;
  getFluentString: GetString;
  onlyShowNewProducts: boolean;
  year: number;
  zIndex: number;
  rootElRef: RefObject<HTMLDivElement>;
}

interface Props {
  variables: Variables;
  extraInputs: ExtraInputs;
}

const chartKey = 'chart';
const tooltipsKey = 'tooltips';
const spinnerKey = 'spinner';

const Container = (props: Props) => {
  const elRef = props.extraInputs.rootElRef;
  const {size} = useDOMElemMonitoring({
    elemRef: elRef,
  });
  const [hoveredProduct, setHoveredProduct] = useState<string | undefined>(undefined);
  const clearHoveredProduct = () => setHoveredProduct(undefined);

  const {extraInputs, variables} = props;
  const queryResult = useQuery<SuccessResponse, Variables>(query, {variables});
  const lastSuccessfulFetch = useLastSuccessfulFetch<SuccessResponse, Variables, ExtraInputs>({
    inputs: {queryResult: {...queryResult, variables}, extraInputs },
    isFetchedDataEmpty: ({treeMapAndTooltip}: SuccessResponse) => treeMapAndTooltip.length === 0,
  });

  let output: React.ReactElement<any> | null;
  let tooltipOutput: React.ReactElement<any> | null;
  if (queryResult.loading === true) {
    if (lastSuccessfulFetch !== undefined && size !== undefined) {
      const {
        location, treeMapAndTooltip, allProductYear,
      } = lastSuccessfulFetch.data;
      const treeMapData = (extraInputs.onlyShowNewProducts === true && location !== null) ?
        filterNewProductsDataToTreeMap(location.newProducts, treeMapAndTooltip) : treeMapAndTooltip;

      const {treeMapCells, tooltipMap}  = transform({
        fetchResult: treeMapData,
        fetchedProductYear: allProductYear,
        width: size.width,
        height: size.height,
        colorCriterion: lastSuccessfulFetch.extraInputs.colorCriterion,
        tradeFlow: lastSuccessfulFetch.extraInputs.tradeFlow,
      });
      output = (
        <>
          <Chart
            highlighted={undefined}
            cells={treeMapCells}
            numCellsTier={NumCellsTier.Small}
            chartContainerHeight={size.height}
            chartContainerWidth={size.width}
            onCellClick={noop}
            onMouseOverCell={noop}
            onMouseLeaveChart={noop}
            key={chartKey}
          />
          <Loading key={spinnerKey}/>
        </>
      );
      tooltipOutput = (
        <TooltipContainer style={{zIndex: extraInputs.zIndex}}>
          <Tooltips
            hovered={undefined}
            highlighted={undefined}
            tooltipMap={tooltipMap}
            clearHighlightTooltip={noop}
            key={tooltipsKey}
          />
        </TooltipContainer>
      );
    } else {
      output = (
        <> <Loading key={spinnerKey}/> </>
      );
      tooltipOutput = null;
    }
  } else if (queryResult.error !== undefined) {
    output = (
      <>
        <ErrorOverlay
          message={extraInputs.getFluentString('general-data-fetch-failure-message')}
        />
      </>
    );
    tooltipOutput = null;
    console.error(queryResult.error);
  } else if (queryResult.data !== undefined) {
    const {
      location, treeMapAndTooltip, allProductYear,
    } = queryResult.data;

    if (extraInputs.onlyShowNewProducts === true && location !== null && location.newProducts.length === 0) {
        output = (
          <NoNewProductContainer>
            <NoNewProductSVG
              dangerouslySetInnerHTML={{__html: require('../newProducts/nonewproduct.svg')}}
            />
            <NoNewProductText>
              {extraInputs.getFluentString('diversification-into-new-products-chart-no-new-products')}
            </NoNewProductText>
          </NoNewProductContainer>
        );
        tooltipOutput = null;
    } else {
      const treeMapData = (extraInputs.onlyShowNewProducts === true && location !== null) ?
        filterNewProductsDataToTreeMap(location.newProducts, treeMapAndTooltip) : treeMapAndTooltip;

      if (size !== undefined) {
        const {treeMapCells, tooltipMap} = transform({
          fetchResult: treeMapData,
        fetchedProductYear: allProductYear,
          width: size.width,
          height: size.height,
          colorCriterion: extraInputs.colorCriterion,
          tradeFlow: extraInputs.tradeFlow,
        });
        output = (
          <Chart
            highlighted={undefined}
            cells={treeMapCells}
            numCellsTier={NumCellsTier.Small}
            chartContainerHeight={size.height}
            chartContainerWidth={size.width}
            onCellClick={noop}
            onMouseOverCell={setHoveredProduct}
            onMouseLeaveChart={clearHoveredProduct}
            key={chartKey}
          />
        );
        tooltipOutput = (
          <TooltipContainer style={{zIndex: extraInputs.zIndex}}>
            <Tooltips
              hovered={hoveredProduct}
              highlighted={undefined}
              tooltipMap={tooltipMap}
              clearHighlightTooltip={noop}
              key={tooltipsKey}
            />
          </TooltipContainer>
        );
      } else {
        output = (<><Loading key={spinnerKey}/></>);
        tooltipOutput = null;
      }
    }
  } else {
    output = null;
    tooltipOutput = null;
  }
  const style: React.CSSProperties = {
    zIndex: extraInputs.zIndex,
  };
  return (
    <>
      <GraphAreaContainer
        ref={elRef}
        style={style}
      >
        {output}
      </GraphAreaContainer>
      {tooltipOutput}
    </>
  );
};

type TreeMapProps = Omit<ExtraInputs, 'rootElRef'> & SlideElementProps;
const TreeMap = React.forwardRef((props: TreeMapProps, rootElRef: RefObject<HTMLDivElement>) => {
  const year = props.year;
  const variables: Variables = {
    facet: TreeMapType.CPY_C,
    productClass: ProductClass.HS,
    year,
    productLevel: ProductLevel.fourDigit,
    locationLevel: null,
    location: props.countryId,
    product: null,
    partner: null,
  };

  return (
    <Container extraInputs={{...props, rootElRef}} variables={variables}/>
  );
});

export default TreeMap;
